import React, { useState, useEffect, useCallback } from 'react';
import { getPosts, createPost, updatePost, getCurrentUser, getPostReactions, getUserPostReactions, toggleReaction, deletePost, deleteComment, validateHashtags, getTrendingHashtags, getPostsByHashtag, getPostHashtags } from '../services/authStore';

const REACTION_TYPES = ['👍', '❤️', '🔥', '🎉'];

const PostCard = ({ post, onSelect, currentUser, onPostDeleted, onEdit }) => {
  const [reactions, setReactions] = useState({});
  const [userReactions, setUserReactions] = useState([]);
  const [shareMessage, setShareMessage] = useState('');
  const [hashtags, setHashtags] = useState([]);

  const authorName = post.profiles?.name || 'Anon dev';
  const authorInitial = authorName.charAt(0).toUpperCase();
  const postedOn = new Date(post.created_at).toLocaleDateString();
  const postUrl = `${window.location.origin}/posts/${post.id}`;
  const visibilityIcon = post.visibility === 'private' ? '🔒' : '🌍';

  const handleShare = async (e) => {
    e.stopPropagation();
    
    try {
      // Try using native Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: `Check out this discussion: "${post.title}"`,
          url: postUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(postUrl);
        setShareMessage('✅ Link copied to clipboard!');
        setTimeout(() => setShareMessage(''), 2000);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const loadReactions = useCallback(async () => {
    const reactionCounts = await getPostReactions(post.id);
    setReactions(reactionCounts);
    
    if (currentUser) {
      const userReactionsList = await getUserPostReactions(post.id, currentUser.id);
      setUserReactions(userReactionsList);
    }
  }, [post.id, currentUser]);

  useEffect(() => {
    loadReactions();
  }, [loadReactions]);

  useEffect(() => {
    const loadHashtags = async () => {
      const tags = await getPostHashtags(post.id);
      setHashtags(tags);
    };
    loadHashtags();
  }, [post.id]);

  const handleReaction = async (e, reactionType) => {
    e.stopPropagation();
    
    if (!currentUser) {
      alert('You must be logged in to react');
      return;
    }

    const result = await toggleReaction({
      postId: post.id,
      userId: currentUser.id,
      reactionType
    });

    if (result.ok) {
      loadReactions();
    }
  };

  return (
    <div
      onClick={onSelect}
      className="card-dark rounded-xl p-4 md:p-6 cursor-pointer transition-all hover:border-green-btn/60 hover:-translate-y-1"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
        <div className="flex-1">
          <div className="flex gap-2 mb-2 items-center flex-wrap text-[11px] text-gray-400">
            <span className="text-[10px] uppercase tracking-widest bg-green-btn text-black px-2 py-1 rounded">
              {post.category}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-btn flex items-center justify-center text-[10px] font-bold text-black">
                {authorInitial}
              </span>
              <span>{authorName}</span>
            </span>
            <span className="text-gray-500">• {postedOn}</span>
            <span className="text-gray-500" title={post.visibility === 'private' ? 'Logged-in users only' : 'Public'}>
              {visibilityIcon}
            </span>
          </div>
          <h3 className="text-xl font-bold mb-2">{post.title}</h3>
          <p className="text-gray-400 line-clamp-2">{post.content}</p>
          {hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {hashtags.map((tag) => (
                <span key={tag.id} className="text-[10px] bg-white/10 text-gray-300 px-2 py-0.5 rounded-full">
                  #{tag.tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2 sm:text-right">
          <div className="flex items-center gap-2 sm:flex-col">
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <div className="text-xl sm:text-2xl font-bold accent-green">{post.comment_count}</div>
          </div>
          <div className="text-[10px] text-gray-500 uppercase hidden sm:block">Click to discuss</div>
        </div>
      </div>

      {/* Edit and Delete buttons */}
      {currentUser && (currentUser.id === post.author_id || currentUser.is_admin) && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={async (e) => {
              e.stopPropagation();
              const postHashtags = await getPostHashtags(post.id);
              onEdit(post, postHashtags);
            }}
            className="text-xs text-green-400 hover:text-green-300 transition"
          >
            ✏️ Edit
          </button>
          {currentUser?.is_admin && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Delete this post? This action cannot be undone.')) {
                  deletePost(post.id).then(result => {
                    if (result.ok) {
                      onPostDeleted(post.id);
                    }
                  });
                }
              }}
              className="text-xs text-red-400 hover:text-red-300 transition"
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* Delete button for admins (if no author match) */}
      {currentUser?.is_admin && currentUser.id !== post.author_id && (
        <div className="mb-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Delete this post? This action cannot be undone.')) {
                deletePost(post.id).then(result => {
                  if (result.ok) {
                    onPostDeleted(post.id);
                  }
                });
              }
            }}
            className="text-xs text-red-400 hover:text-red-300 transition"
          >
            Delete Post
          </button>
        </div>
      )}

      {/* Reactions */}
      <div className="flex gap-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
        {REACTION_TYPES.map((reactionType) => (
          <button
            key={reactionType}
            onClick={(e) => handleReaction(e, reactionType)}
            className={`px-3 py-1 rounded-full text-sm transition-all ${
              userReactions.includes(reactionType)
                ? 'bg-green-btn text-black'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            {reactionType} {reactions[reactionType] || 0}
          </button>
        ))}
        <button
          onClick={handleShare}
          className="px-3 py-1 rounded-full text-sm bg-white/10 text-gray-300 hover:bg-white/20 transition-all flex items-center gap-1"
          title="Share this post"
        >
          <span>📤</span> Share
        </button>
      </div>
      {shareMessage && (
        <div className="mt-3 text-xs text-green-400">
          {shareMessage}
        </div>
      )}
    </div>

  );
};
const Forum = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', category: 'general', visibility: 'public', hashtags: '' });
  const [message, setMessage] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('trending');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showMyThreads, setShowMyThreads] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editFormData, setEditFormData] = useState({ title: '', content: '', category: 'general', visibility: 'public', hashtags: '' });

  const checkUser = useCallback(async () => {
    const user = await getCurrentUser();
    setCurrentUser(user);
  }, []);

  const fetchPosts = useCallback(async (user) => {
    setLoading(true);
    const result = await getPosts(20, 0, user);
    if (result.ok) {
      setPosts(result.posts);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkUser().then((user) => {
      // After checking user, fetch posts with the user data
      getCurrentUser().then((currentUser) => {
        fetchPosts(currentUser);
      });
    });
    
    // Fetch trending hashtags
    const loadTrendingHashtags = async () => {
      const tags = await getTrendingHashtags(10);
      setTrendingHashtags(tags);
    };
    loadTrendingHashtags();
  }, [checkUser, fetchPosts]);

  // Handle hashtag filtering
  useEffect(() => {
    if (selectedHashtag) {
      const filterByHashtag = async () => {
        setLoading(true);
        const result = await getPostsByHashtag(selectedHashtag, 20, 0, currentUser);
        if (result.ok) {
          setPosts(result.posts);
        }
        setLoading(false);
      };
      filterByHashtag();
    } else {
      // Reload all posts if hashtag is deselected
      fetchPosts(currentUser);
    }
  }, [selectedHashtag, currentUser, fetchPosts]);

  const handlePostDeleted = (postId) => {
    setPosts(posts.filter(p => p.id !== postId));
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setMessage('❌ You must be logged in to create a post');
      return;
    }

    if (!formData.title.trim() || !formData.content.trim()) {
      setMessage('❌ Title and content are required');
      return;
    }

    // Validate hashtags
    const hashtags = formData.hashtags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
      .map(tag => tag.startsWith('#') ? tag.substring(1) : tag);
    
    const hashtagValidation = validateHashtags(hashtags);
    if (!hashtagValidation.ok) {
      setMessage(`❌ ${hashtagValidation.error}`);
      return;
    }

    const result = await createPost({
      title: formData.title,
      content: formData.content,
      category: formData.category,
      authorId: currentUser.id,
      visibility: formData.visibility,
      hashtags: hashtagValidation.tags
    });

    if (result.ok) {
      setMessage('✅ Post created successfully!');
      setFormData({ title: '', content: '', category: 'general', visibility: 'public', hashtags: '' });
      setShowCreateForm(false);
      // Fetch user profile and attach to post
      const { getUserProfile } = require('../services/authStore');
      const userProfile = await getUserProfile(currentUser.id);
      const postWithAuthor = {
        ...result.post,
        profiles: userProfile
      };
      setPosts((prev) => [postWithAuthor, ...prev]);
      // Refresh trending hashtags
      const tags = await getTrendingHashtags(10);
      setTrendingHashtags(tags);
    } else {
      setMessage(`❌ ${result.error}`);
    }
  };

  const handleEditPost = async (e) => {
    e.preventDefault();
    
    if (!editingPost || !currentUser) {
      setMessage('❌ Error editing post');
      return;
    }

    if (!editFormData.title.trim() || !editFormData.content.trim()) {
      setMessage('❌ Title and content are required');
      return;
    }

    // Validate hashtags
    const hashtags = editFormData.hashtags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
      .map(tag => tag.startsWith('#') ? tag.substring(1) : tag);
    
    const hashtagValidation = validateHashtags(hashtags);
    if (!hashtagValidation.ok) {
      setMessage(`❌ ${hashtagValidation.error}`);
      return;
    }

    const result = await updatePost({
      postId: editingPost.id,
      title: editFormData.title,
      content: editFormData.content,
      category: editFormData.category,
      visibility: editFormData.visibility,
      hashtags: hashtagValidation.tags
    });

    if (result.ok) {
      setMessage('✅ Post updated successfully!');
      // Update the posts list
      setPosts((prev) =>
        prev.map((p) =>
          p.id === editingPost.id
            ? { ...p, ...result.post }
            : p
        )
      );
      // Update selected post if it's currently selected
      setSelectedPost((prev) =>
        prev && prev.id === editingPost.id
          ? { ...prev, ...result.post }
          : prev
      );
      setEditingPost(null);
      // Refresh trending hashtags
      const tags = await getTrendingHashtags(10);
      setTrendingHashtags(tags);
    } else {
      setMessage(`❌ ${result.error}`);
    }
  };

  const handleCommentAdded = (postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p
      )
    );
    setSelectedPost((prev) =>
      prev && prev.id === postId
        ? { ...prev, comment_count: (prev.comment_count || 0) + 1 }
        : prev
    );
  };

  const filterAndSortPosts = () => {
    let list = [...posts];
    if (showMyThreads && currentUser) {
      list = list.filter((p) => p.author_id === currentUser.id);
    }
    if (categoryFilter !== 'all') {
      list = list.filter((p) => p.category === categoryFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (p) => p.title.toLowerCase().includes(q) || p.content.toLowerCase().includes(q)
      );
    }
    if (sortKey === 'trending') {
      list.sort((a, b) => (b.comment_count || 0) - (a.comment_count || 0));
    } else {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return list;
  };

  return (
    <div className="min-h-screen bg-black pt-6 pb-20 lg:pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10">

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">Developer Health <span className="accent-green">Forum</span></h1>
          <p className="text-gray-400 text-sm sm:text-base md:text-lg">Share your health journey, discuss energy management, RSI fixes, and sustainable deep work.</p>
        </div>

        {!currentUser && (
          <div className="card-dark rounded-xl p-6 mb-6 border border-green-btn/30">
            <p className="text-gray-300">Sign in above to create a post and join the discussion.</p>
          </div>
        )}

        {/* Edit Post Form */}
        {editingPost && currentUser && (
          <div className="card-dark rounded-xl p-6 mb-8 border border-green-btn/30">
            <form onSubmit={handleEditPost} className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Edit Post</h2>
                <button
                  type="button"
                  onClick={() => setEditingPost(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  placeholder="What's on your mind about developer health?"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Category</label>
                <select
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                >
                  <option value="general">General Health</option>
                  <option value="energy">Energy & Nutrition</option>
                  <option value="rsi">RSI & Ergonomics</option>
                  <option value="focus">Focus & Productivity</option>
                  <option value="sleep">Sleep & Recovery</option>
                  <option value="fitness">Fitness & Exercise</option>
                  <option value="mental">Mental Health</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Post Visibility</label>
                <select
                  value={editFormData.visibility}
                  onChange={(e) => setEditFormData({ ...editFormData, visibility: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                >
                  <option value="public">🌍 Public (Anyone can view)</option>
                  <option value="private">🔒 Logged-in Only (Members only)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Hashtags (max 3, comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., mental-health, productivity, burnout"
                  value={editFormData.hashtags}
                  onChange={(e) => setEditFormData({ ...editFormData, hashtags: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                />
                <p className="text-xs text-gray-400 mt-1">Tip: Use keywords that describe your topic</p>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Your Discussion</label>
                <textarea
                  placeholder="Share your thoughts, tips, questions, or experiences..."
                  value={editFormData.content}
                  onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b] min-h-[200px]"
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-green-btn text-black font-bold py-3 rounded-lg hover:scale-105 transition"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPost(null)}
                  className="flex-1 bg-white/10 text-gray-300 font-bold py-3 rounded-lg hover:bg-white/20 transition"
                >
                  Cancel
                </button>
              </div>

              {message && (
                <p className={`text-sm ${message.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>{message}</p>
              )}
            </form>
          </div>
        )}

        {/* Create Post Form */}
        {showCreateForm && currentUser && (
          <div className="card-dark rounded-xl p-6 mb-8">
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  placeholder="What's on your mind about developer health?"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                >
                  <option value="general">General Health</option>
                  <option value="energy">Energy & Nutrition</option>
                  <option value="rsi">RSI & Ergonomics</option>
                  <option value="focus">Focus & Productivity</option>
                  <option value="sleep">Sleep & Recovery</option>
                  <option value="fitness">Fitness & Exercise</option>
                  <option value="mental">Mental Health</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Post Visibility</label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                >
                  <option value="public">🌍 Public (Anyone can view)</option>
                  <option value="private">🔒 Logged-in Only (Members only)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Hashtags (max 3, comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., mental-health, productivity, burnout"
                  value={formData.hashtags}
                  onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b]"
                />
                <p className="text-xs text-gray-400 mt-1">Tip: Use keywords that describe your topic</p>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Your Discussion</label>
                <textarea
                  placeholder="Share your thoughts, tips, questions, or experiences..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg text-white focus:outline-none focus:border-[#50fa7b] min-h-[200px]"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-btn text-black font-bold py-3 rounded-lg hover:scale-105 transition"
              >
                Publish Discussion
              </button>

              {message && (
                <p className={`text-sm ${message.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>{message}</p>
              )}
            </form>
          </div>
        )}

        {currentUser && (
          <div className="mb-6 flex gap-3 items-center flex-wrap">
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-6 py-3 min-h-[44px] bg-green-btn text-black font-bold rounded-lg hover:scale-105 transition touch-manipulation"
            >
              {showCreateForm ? 'Cancel' : '✏️ Start a Discussion'}
            </button>
            <button
              onClick={() => setShowMyThreads(!showMyThreads)}
              className={`px-4 py-3 min-h-[44px] text-xs font-bold rounded-lg transition ${showMyThreads ? 'bg-green-btn text-black' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
            >
              {showMyThreads ? '✓ My Threads' : 'My Threads'}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-3 min-h-[44px] rounded-lg transition font-bold text-lg ${showFilters ? 'bg-green-btn text-black' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
              title="Toggle filters"
            >
              ⚙️
            </button>
          </div>
        )}

        {!currentUser && (
          <div className="mb-6 flex gap-3 items-center flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-3 min-h-[44px] rounded-lg transition font-bold text-lg ${showFilters ? 'bg-green-btn text-black' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
              title="Toggle filters"
            >
              ⚙️
            </button>
          </div>
        )}

        {/* Trending Hashtags */}
        {trendingHashtags.length > 0 && (
          <div className="mb-6 p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-bold text-gray-300">🔥 Trending:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {trendingHashtags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setSelectedHashtag(selectedHashtag === tag.tag ? null : tag.tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                    selectedHashtag === tag.tag
                      ? 'bg-green-btn text-black'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                  title={`${tag.usage_count} posts`}
                >
                  #{tag.tag} <span className="text-[10px] ml-1 opacity-70">({tag.usage_count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Collapsible Filters */}
        {showFilters && (
          <div className="card-dark rounded-xl p-2 sm:p-3 md:p-4 mb-6 border border-white/5 space-y-1.5 sm:space-y-2 animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-3 w-full">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-black border border-[#2d2d2d] rounded text-white focus:outline-none focus:border-[#50fa7b]"
              />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-black border border-[#2d2d2d] rounded text-white focus:outline-none focus:border-[#50fa7b]"
              >
                <option value="all">All</option>
                <option value="general">Health</option>
                <option value="energy">Energy</option>
                <option value="rsi">RSI</option>
                <option value="focus">Focus</option>
                <option value="sleep">Sleep</option>
                <option value="fitness">Fitness</option>
                <option value="mental">Mental</option>
              </select>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-black border border-[#2d2d2d] rounded text-white focus:outline-none focus:border-[#50fa7b]"
              >
                <option value="trending">Trending</option>
                <option value="new">Newest</option>
              </select>
            </div>
          </div>
        )}

        {/* Posts List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map((i) => (
              <div key={i} className="card-dark rounded-xl p-6 border border-[#1d1d1d] animate-pulse">
                <div className="h-3 w-24 bg-white/10 rounded mb-3"></div>
                <div className="h-4 w-3/4 bg-white/10 rounded mb-2"></div>
                <div className="h-3 w-full bg-white/5 rounded mb-2"></div>
                <div className="h-3 w-5/6 bg-white/5 rounded"></div>
              </div>
            ))}
          </div>
        ) : filterAndSortPosts().length === 0 ? (
          <div className="card-dark rounded-xl p-8 text-center border border-white/10">
            <p className="text-gray-400 text-lg">No discussions match your filters.</p>
            <p className="text-gray-500 text-sm mt-2">Try clearing search or switching category.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filterAndSortPosts().map((post) => (
              <PostCard key={post.id} post={post} onSelect={() => setSelectedPost(post)} currentUser={currentUser} onPostDeleted={handlePostDeleted} onEdit={(post, hashtags) => {
                setEditingPost(post);
                setEditFormData({
                  title: post.title,
                  content: post.content,
                  category: post.category,
                  visibility: post.visibility,
                  hashtags: hashtags.map(h => h.tag).join(', ')
                });
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Modal for post detail */}
      {selectedPost && (
        <PostDetail
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          currentUser={currentUser}
          onCommentAdded={handleCommentAdded}
          onPostDeleted={handlePostDeleted}
          onEdit={(post, hashtags) => {
            setEditingPost(post);
            setEditFormData({
              title: post.title,
              content: post.content,
              category: post.category,
              visibility: post.visibility,
              hashtags: hashtags.map(h => h.tag).join(', ')
            });
          }}
        />
      )}
    </div>
  );
};

const PostDetail = ({ post, onClose, currentUser, onCommentAdded, onPostDeleted, onEdit }) => {
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState({});
  const [userReactions, setUserReactions] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [postCommentCount, setPostCommentCount] = useState(post.comment_count);
  const [shareMessage, setShareMessage] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const { getPostComments, addComment } = require('../services/authStore');

  const formatDate = (iso) => new Date(iso).toLocaleDateString();
  const getInitial = (text) => (text?.trim()?.charAt(0)?.toUpperCase() || 'D');
  const totalReactions = Object.values(reactions || {}).reduce((sum, val) => sum + (val || 0), 0);
  const postUrl = `${window.location.origin}/posts/${post.id}`;
  const visibilityIcon = post.visibility === 'private' ? '🔒' : '🌍';

  const handleShare = async (e) => {
    e.stopPropagation();
    
    try {
      // Try using native Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: `Check out this discussion: "${post.title}"`,
          url: postUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(postUrl);
        setShareMessage('✅ Link copied to clipboard!');
        setTimeout(() => setShareMessage(''), 2000);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const loadHashtags = async () => {
    const tags = await getPostHashtags(post.id);
    setHashtags(tags);
  };

  const loadComments = async () => {
    const commentsData = await getPostComments(post.id);
    setComments(commentsData);
  };

  const loadReactions = async () => {
    const reactionCounts = await getPostReactions(post.id);
    setReactions(reactionCounts);
    
    if (currentUser) {
      const userReactionsList = await getUserPostReactions(post.id, currentUser.id);
      setUserReactions(userReactionsList);
    }
  };

  const handleReaction = async (reactionType) => {
    if (!currentUser) {
      alert('You must be logged in to react');
      return;
    }

    const result = await toggleReaction({
      postId: post.id,
      userId: currentUser.id,
      reactionType
    });

    if (result.ok) {
      loadReactions();
    }
  };

  useEffect(() => {
    setPostCommentCount(post.comment_count || 0);
  }, [post.id, post.comment_count]);

  useEffect(() => {
    loadComments();
    loadReactions();
    loadHashtags();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('You must be logged in to comment');
      return;
    }
    if (!newComment.trim()) return;

    const { getUserProfile } = require('../services/authStore');
    const result = await addComment({
      postId: post.id,
      content: newComment,
      authorId: currentUser.id
    });

    if (result.ok) {
      setNewComment('');
      setPostCommentCount(prev => prev + 1);
      // Fetch user profile and attach to comment
      const userProfile = await getUserProfile(currentUser.id);
      const commentWithAuthor = {
        ...result.comment,
        profiles: userProfile
      };
      setComments((prev) => [...prev, commentWithAuthor]);
      if (onCommentAdded) {
        onCommentAdded(post.id);
      }
      // Auto-close modal after comment
      setTimeout(() => onClose(), 1500);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-dark rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-black border-b border-white/10 p-6 flex justify-between items-start gap-4">
            <h2 className="text-2xl font-bold flex-1">{post.title}</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleShare}
                className="px-3 py-2 rounded-lg text-sm bg-white/10 text-gray-300 hover:bg-white/20 transition-all flex items-center gap-1 whitespace-nowrap"
                title="Share this post"
              >
                <span>📤</span> Share
              </button>
              {currentUser && (currentUser.id === post.author_id || currentUser.is_admin) && (
                <button
                  onClick={async () => {
                    const postHashtags = await getPostHashtags(post.id);
                    onEdit(post, postHashtags);
                    onClose();
                  }}
                  className="text-green-400 hover:text-green-300 font-mono text-sm"
                >
                  Edit
                </button>
              )}
              {currentUser?.is_admin && (
                <button
                  onClick={async () => {
                    if (window.confirm('Delete this post? This action cannot be undone.')) {
                      const result = await deletePost(post.id);
                      if (result.ok) {
                        onPostDeleted?.(post.id);
                        onClose();
                      } else {
                        alert(`Error: ${result.error}`);
                      }
                    }
                  }}
                  className="text-red-400 hover:text-red-300 font-mono text-sm"
                >
                  Delete
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

        <div className="p-6 space-y-6">
          {/* Post content */}
          <div>
            <span className="text-[10px] uppercase tracking-widest bg-green-btn text-black px-2 py-1 rounded">
              {post.category}
            </span>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
              <span className="bg-white/5 px-2 py-1 rounded">{postCommentCount} comments</span>
              <span className="bg-white/5 px-2 py-1 rounded">{totalReactions} reactions</span>
              <span className="bg-white/5 px-2 py-1 rounded">Posted {formatDate(post.created_at)}</span>
              <span className="bg-white/5 px-2 py-1 rounded" title={post.visibility === 'private' ? 'Logged-in users only' : 'Public'}>
                {visibilityIcon} {post.visibility === 'private' ? 'Private' : 'Public'}
              </span>
            </div>
            {shareMessage && (
              <div className="mt-3 p-2 bg-green-btn/20 text-green-400 text-xs rounded">
                {shareMessage}
              </div>
            )}
            <p className="text-gray-300 text-sm mt-2 leading-relaxed whitespace-pre-wrap">{post.content}</p>
            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {hashtags.map((tag) => (
                  <span key={tag.id} className="text-xs bg-white/10 text-gray-300 px-2.5 py-0.5 rounded-full">
                    #{tag.tag}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-[12px] text-gray-400 mt-3">
              <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[11px] font-bold">
                {(post.profiles?.name || 'Anon dev').charAt(0).toUpperCase()}
              </span>
              <span>{post.profiles?.name || 'Anon dev'}</span>
              <span className="text-gray-500">• {formatDate(post.created_at)}</span>
            </div>
          </div>

          {/* Reactions */}
          <div className="flex gap-3 flex-wrap border-t border-white/10 pt-4">
            {REACTION_TYPES.map((reactionType) => (
              <button
                key={reactionType}
                onClick={() => handleReaction(reactionType)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  userReactions.includes(reactionType)
                    ? 'bg-green-btn text-black'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {reactionType} {reactions[reactionType] || 0}
              </button>
            ))}
          </div>

          <div className="border-t border-white/10"></div>

          {/* Comments section */}
          <div>
            <div className="flex items-center justify-between gap-4 mb-3">
              <h3 className="text-xl font-bold">{postCommentCount} Comments</h3>
              <span className="text-[11px] text-gray-500">Be kind and stay on topic.</span>
            </div>

            {/* Add comment form */}
            {currentUser ? (
              <form onSubmit={handleAddComment} className="mb-6 space-y-3 bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-accent-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="text-xs font-semibold text-gray-300">Add your comment</span>
                </div>
                <textarea
                  placeholder="Share a tip, experience, or follow-up question..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-[#2d2d2d] rounded text-white focus:outline-none focus:border-[#50fa7b] min-h-[120px]"
                />
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-gray-500">Markdown not supported yet.</p>
                  <button
                    type="submit"
                    disabled={!newComment.trim()}
                    className={`px-4 py-2 font-bold rounded transition flex items-center gap-2 ${
                      newComment.trim()
                        ? 'bg-green-btn text-black hover:scale-105'
                        : 'bg-white/10 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Post Comment
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg p-4 mb-6 flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <p className="text-gray-400 text-sm">Sign in above to share your thoughts and join the discussion</p>
              </div>
            )}

            {/* Comments list */}
            {loading ? (
              <p className="text-gray-400">Loading comments...</p>
            ) : comments.length === 0 ? (
              <p className="text-gray-400 text-sm">No comments yet. Be the first to respond!</p>
            ) : (
              <div className="space-y-4">
                {comments.map((comment) => {
                  const name = comment.profiles?.name || 'Anon dev';
                  const initial = getInitial(name || comment.content);
                  return (
                    <div key={comment.id} className="bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg p-4 flex gap-3">
                      <div className="w-9 h-9 rounded-full bg-green-btn flex items-center justify-center text-xs font-bold flex-shrink-0 text-black">
                        {initial}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-white">{name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-[11px] text-gray-500">{formatDate(comment.created_at)}</span>
                            {currentUser?.is_admin && (
                              <button
                                onClick={async () => {
                                  if (window.confirm('Delete this comment?')) {
                                    const result = await deleteComment(comment.id);
                                    if (result.ok) {
                                      setComments(comments.filter(c => c.id !== comment.id));
                                      setPostCommentCount(prev => prev - 1);
                                    }
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 text-xs font-mono"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed mt-1">{comment.content}</p>
                        {/* Comment reactions */}
                        <div className="flex gap-2 flex-wrap mt-2" onClick={(e) => e.stopPropagation()}>
                          {REACTION_TYPES.map((reactionType) => (
                            <button
                              key={`${comment.id}-${reactionType}`}
                              onClick={async () => {
                                if (!currentUser) {
                                  alert('Sign in to react to comments');
                                  return;
                                }
                                // Toggle reaction on comment
                                const newComments = comments.map((c) => {
                                  if (c.id === comment.id) {
                                    const reactions = c.reactions || {};
                                    reactions[reactionType] = (reactions[reactionType] || 0) + (c.userReaction?.[reactionType] ? -1 : 1);
                                    return { ...c, reactions, userReaction: { ...c.userReaction, [reactionType]: !c.userReaction?.[reactionType] } };
                                  }
                                  return c;
                                });
                                setComments(newComments);
                              }}
                              className={`px-2 py-0.5 rounded text-xs transition-all ${
                                comment.userReaction?.[reactionType]
                                  ? 'bg-green-btn text-black'
                                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
                              }`}
                            >
                              {reactionType} {comment.reactions?.[reactionType] || 0}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forum;
